import { HelpCircle } from '@untitled-ui/icons-react';
import { Check } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import moment from 'moment';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Decorator, Input } from '@myhealth-raccoon/components';
import { validation } from '@myhealth-raccoon/forms';

import { PopoverTooltip } from '../PopoverTooltip';
import MedicareLogo from 'src/assets/Medicare-Logo.svg';
import MedicareCardHelp from 'src/assets/medicarecardhelp.png';
import Sparkle from 'src/assets/sparkle.svg?react';
import { useRememberFormValues } from 'src/hooks/useRememberFormValues';
import { MedicareCardStatus, PatientHasDetails, PatientUpdateMedicare } from 'src/services/patient';

type Props = {
    shouldShowValidation?: boolean;
    className?: string;
    prepend?: React.ReactNode;
    append?: React.ReactNode;
    required?: boolean;
    patientDetails: PatientHasDetails;
    myMedicareRegisterPath?: string;
};

type Form = PatientUpdateMedicare & {
    haveMedicare: 'yes' | 'no';
    expiry: string;
};

export interface MedicareFormRef {
    submit(): Promise<Form>;
}

function renderStatus(status: MedicareCardStatus) {
    switch (status) {
        case MedicareCardStatus.Missing:
            return <span className="text-error">Missing</span>;
        case MedicareCardStatus.Invalid:
            return <span className="text-error">Invalid</span>;
        case MedicareCardStatus.Expired:
            return <span className="text-error">Expired</span>;
        case MedicareCardStatus.Valid:
            return <span>Not expired</span>;
    }
}

export const MedicareForm = forwardRef<MedicareFormRef, Props>(
    ({ shouldShowValidation, className, prepend, append, required, patientDetails, myMedicareRegisterPath }, ref) => {
        const [defaultValues, rememberDefaultValues] = useRememberFormValues<Form>('medicareForm', 'session');
        const [editing, setEditing] = useState(patientDetails.medicareDetails.status !== MedicareCardStatus.Valid);

        const haveMedicare =
            patientDetails.medicareDetails?.status !== MedicareCardStatus.Missing ? 'yes' : required ? undefined : 'no';

        const {
            register,
            watch,
            formState: { errors },
            handleSubmit,
            reset,
        } = useForm<Form>({
            defaultValues: defaultValues ?? {
                haveMedicare: haveMedicare,
                number: '',
                irn: '',
                expiry: undefined,
            },
        });

        const medicareRadio = register('haveMedicare', { validate: required ? validation.required() : undefined });

        // Update form values whenever patientDetails finishes loading/updating
        useEffect(() => {
            if (patientDetails) {
                reset({
                    haveMedicare: haveMedicare,
                    number: '',
                    irn: '',
                    expiry: undefined,
                });
            }
        }, [haveMedicare, patientDetails, required, reset]);

        useImperativeHandle(ref, () => ({
            submit: async () => {
                return new Promise((resolve, reject) => {
                    handleSubmit(async (values) => {
                        rememberDefaultValues(values);
                        await resolve(values);
                    }, reject)();
                });
            },
        }));

        const validateExpiry = useCallback((value: unknown): string | undefined => {
            if (typeof value !== 'string') {
                return undefined;
            }
            const parsed = moment(value, 'YYYY-DD');
            if (
                !parsed.isValid() ||
                parsed.isAfter(moment().add(10, 'year')) ||
                parsed.isBefore(moment().subtract(10, 'year'))
            ) {
                return 'Enter a valid date';
            }
            return undefined;
        }, []);

        return (
            <>
                {prepend}
                <form className={classNames('space-y-2', className)}>
                    {patientDetails.medicareDetails.status === MedicareCardStatus.Missing ? (
                        <Decorator
                            fieldset
                            label={
                                <>
                                    <div className="flex flex-row gap-2">
                                        Do you have a Medicare card?
                                        {required && (
                                            <span aria-label="Required" className="text-primary">
                                                *
                                            </span>
                                        )}
                                        <PopoverTooltip placement="bottom" content={<img src={MedicareCardHelp} />}>
                                            <HelpCircle className="size-5 text-gray-400" />
                                        </PopoverTooltip>
                                    </div>
                                </>
                            }
                            fieldError={shouldShowValidation ? errors.haveMedicare : undefined}
                        >
                            <div className="flex flex-row gap-5 py-1.5 text-base">
                                <Decorator className="flex flex-row gap-3">
                                    <input
                                        type="radio"
                                        className="radio mt-0.5 align-bottom text-base"
                                        value="yes"
                                        {...medicareRadio}
                                        id="haveMedicareTrue"
                                    />
                                    <label htmlFor="haveMedicareTrue" className="cursor-pointer text-gray-700">
                                        Yes
                                    </label>
                                </Decorator>
                                <Decorator className="flex flex-row gap-3">
                                    <input
                                        type="radio"
                                        className="radio mt-0.5 align-bottom text-base"
                                        value="no"
                                        {...medicareRadio}
                                        id="haveMedicareFalse"
                                    />
                                    <label htmlFor="haveMedicareFalse" className="cursor-pointer text-gray-700">
                                        No
                                    </label>
                                </Decorator>
                            </div>
                        </Decorator>
                    ) : (
                        <input type="hidden" {...medicareRadio} value="yes" />
                    )}
                    {!editing && patientDetails.medicareDetails.status !== MedicareCardStatus.Missing && (
                        <div className="mt-5 flex flex-row items-start gap-2 rounded-xl border bg-gray-50 px-4 py-5">
                            <div className="flex w-full flex-col items-start gap-2">
                                {patientDetails.myMedicare && (
                                    <div className="border-success-200 bg-success-50 flex flex-row items-center gap-1 rounded-xl border px-2 py-0.5">
                                        <Check className="size-3 font-medium [&_path]:stroke-green-700" />
                                        <p className="text-sm font-medium text-green-700">MyMedicare enrolled</p>
                                    </div>
                                )}
                                <div className="flex flex-col">
                                    <p className="text-sm font-medium text-gray-700">
                                        Medicare ending in {patientDetails.medicareDetails.maskedNumber}
                                    </p>
                                    <p className="text-sm">{renderStatus(patientDetails.medicareDetails.status)}</p>
                                </div>
                                <button
                                    type="button"
                                    className="text-primary text-sm font-semibold hover:underline disabled:pointer-events-none disabled:text-gray-400"
                                    onClick={() => setEditing(true)}
                                >
                                    Edit
                                </button>
                            </div>
                            <img src={MedicareLogo} className="ml-auto block" alt="medicare" />
                        </div>
                    )}

                    {watch('haveMedicare') === 'yes' && editing && (
                        <div className="mt-5 flex flex-col items-start gap-5 rounded-xl border bg-gray-50 px-4 py-5">
                            <Input
                                className="w-full"
                                {...register('number', {
                                    validate: validation.composeValidators(
                                        validation.required(),
                                        validation.medicare()
                                    ),
                                })}
                                type="text"
                                inputMode="numeric"
                                label="Medicare number"
                                fieldError={shouldShowValidation ? errors.number : undefined}
                            />
                            <div className="flex w-full flex-row flex-wrap gap-x-2 gap-y-5">
                                <Input
                                    className="flex-1"
                                    {...register('irn', {
                                        validate: validation.composeValidators(
                                            validation.required(),
                                            validation.min(0),
                                            validation.maxLength(1)
                                        ),
                                    })}
                                    type="number"
                                    inputMode="numeric"
                                    label="IRN"
                                    fieldError={shouldShowValidation ? errors.irn : undefined}
                                />
                                <Input
                                    className="flex-1"
                                    {...register('expiry', {
                                        validate: validation.composeValidators(validation.required(), validateExpiry),
                                    })}
                                    type="month"
                                    label="Expiry"
                                    fieldError={shouldShowValidation ? errors.expiry : undefined}
                                />
                            </div>
                            <button
                                type="button"
                                className="font-semibold text-gray-600 hover:underline disabled:pointer-events-none disabled:text-gray-400"
                                onClick={() => {
                                    setEditing(false);
                                    reset({ haveMedicare: watch('haveMedicare') });
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                    {watch('haveMedicare') === 'yes' &&
                        !patientDetails.myMedicare &&
                        [MedicareCardStatus.Valid, MedicareCardStatus.Expired].includes(
                            patientDetails.medicareDetails.status
                        ) && (
                            <div
                                className={
                                    'border-primary-800 flex flex-col items-start gap-3 rounded-lg border bg-gradient-to-r from-[#FFF8F4] to-[#FFFFFF] p-4 shadow-sm shadow-[#101828]/5 ring-2 ring-[#FDDBC4]'
                                }
                            >
                                <div className={'flex flex-col items-start gap-2'}>
                                    <div className={'flex flex-row items-center gap-2'}>
                                        <div>
                                            <Sparkle />
                                        </div>
                                        <p className={'text-sm font-semibold text-gray-900'}>
                                            You're eligible for MyMedicare!
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm">
                                            Enrol to MyMedicare with our clinic to gain access to additional benefits
                                            that enhance your healthcare journey.
                                        </p>
                                    </div>
                                </div>
                                <div className={'flex flex-row gap-3'}>
                                    {myMedicareRegisterPath && (
                                        <div>
                                            <a
                                                className={'text-primary text-sm font-semibold'}
                                                href={myMedicareRegisterPath}
                                                target="_blank"
                                            >
                                                Register now
                                            </a>
                                        </div>
                                    )}
                                    <div>
                                        <a
                                            className={'text-sm font-semibold text-gray-600'}
                                            href={'https://www.myhealth.net.au/myhealth-enrolment/'}
                                            target="_blank"
                                        >
                                            Learn More
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}
                </form>
                {append}
            </>
        );
    }
);
